import React, {
  useEffect,
  useCallback,
  useState,
  useReducer,
  useRef,
  useLayoutEffect,
  useContext,
  useImperativeHandle,
  forwardRef,
  useMemo
} from 'react';
import fetchAPI from '../../utils/fetchAPI';
import log from '../../utils/log';
import { FormContext } from '../form/FormContext';
import updateHiddenFieldsOnDOM, {
  initHiddenFieldsOnDOM
} from '../form/updateHiddenFieldsOnDOM';
import {
  mapSectionsToFields,
  getButtons2Show,
  doesUITypeHaveSubfields
} from '../../utils/formUtils';
import updateCollapsibleSections from '../form/updateCollapsibleSections';
import updateDescriptionsOnDOM, {
  initFieldDescriptions,
  initFieldDescriptionsOnDOM
} from '../form/updateDescriptionsOnDOM';
import FormView from '../view/FormView';
import getApiEndpoints, {
  EndpointTypes,
  getEndpointType
} from '../form/FormEndpoints';
import seperateAdminFromEuFields from '../form/seperateAdminFromEuFields';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { setState as setSaveChangesState, States } from '../form/SaveChanges';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import useTranslations from '../../hooks/useTranslations';
import useGlobalVariables from '../../hooks/useGlobalVariables';

import _isEqual from 'lodash.isequal';
import { Sizes } from '../layouts/ng-layouts/Containers';
import {
  initTextReplacements,
  updateTextReplacements
} from './updateTextReplacements';
import { useHistory } from 'react-router';
import getJWT from '../../utils/getJWT';
import {
  mergeButtons,
  SYSTEM_BUTTONS_DEFAULT_SCHEMA,
  INTEGRATION_BUTTONS_DEFAULT_SCHEMA
} from './FormBuilderCustomButtonsEngine';
import { reject } from 'core-js/fn/promise';
import formatIfNumber from '../../utils/formatIfNumber';
import Colors from '../../internal-components/Colors';
import useTimer from '../../hooks/useTimer';
import getFormStatusesById from '../../utils/getFormStatusesById';
import { isViewOnlyAdminOfForm } from '../../utils/formUtils';

gsap.registerPlugin(ScrollToPlugin);

const AUTOSAVE_DELAY_THRESHOLD = 1000; // in ms
const CALCULATION_DELAY_THRESHOLD = 1000;

const defaultFormConfigurations = {
  useValidation: true,
  showAdminFields: false,
  isReadOnlyFieldsEditable: false,
  useAutoSave: true,
  isFormReadOnly: false,
  endpointType: undefined // optional to pick from EndpointTypes (../form/FormEndpoints), if null, will try to pick automatically
};

const FormSharingTypes = {
  PUBLIC: 'anonymous',
  PRIVATE: 'internal',
  INVITE_ONLY: 'assigned'
};

export const Variants = {
  OT_CLASSIC: 'ot_c',
  MOBILE: 'mobile'
};

const CREDAS_CHECK_ADMIN_REQUIRED_FIELDS = ['checktype', 'title'];
const CREDAS_FULL_ID_CHECK_ADMIN_REQUIRED_FIELDS = ['contactVia'];

const FormReducer = (state, action) => {
  const { type, payload, index } = action;

  switch (type) {
    case 'update':
      return { ...state, ...payload };

    default:
      return state;
  }
};

const InitalReducerState = {
  form: {},
  userSubmission: {},
  docusign: false,
  title: '',
  description: '',
  sharingType: '',
  fields: [],
  mappedFields: {}, // holds the same values as fields but mapped to the field inernal id (search field node by field id in O(1))
  euFieldsIds: [],
  adminFieldsIds: [],
  userInput: {},
  rules: [],
  buttons: { systemButtons: [], customButtons: [], integrationButtons: [] },
  viewOnlyEndUserSide: false,
  integrationsList: []
};

/**
 *
 * @param {String} formId - The form ID
 * @param {String} submissionId - If passed, and existing submissions wil be loaded onto the form
 * @param {Object} config - A configuration object used to set the form behavior (default and possible values can be seen as destructed in props)
 * @param {Function} setIsDirty - A function for the form to use in order to inform that the form is dirty
 * @param {Function} onDataLoaded - A callback to be called whenever te data is done loading. Will return the form initial data
 * @param {Function} onSubmit - A callback to be called after the submit was successfull
 * @param {Function} onBlur - A callback to be called on form blur
 * @param {Function} onPrevOrNext - A callback to be called when next or prev submission buttons clicked
 * @param {Function} onSave - A callback to be called after the save was successfull
 * @param {Function} exportForDocusign - A callback to create the PDF for docusign
 * @param {Function} beforeSaveChanges - A callback to be called before save changes is being called
 * @param {Boolean} noSubmit - Pass true if we don't want to show the submit button
 * @param {Boolean} noSaveBtn - Pass true if we don't want to show the save changes button (save change button will show if autosave is disabled)
 * @param {String} variant - Choose a variant from Variants to apply that variant form style, by default will use OT_CLASSIC
 * @param {Boolean} hideFormTitle - If true - will not show form title
 * @param {Boolean} hideFormDescription - If true - will not show form description
 * @param {Node} thankUCmp - a node to replace the tnak you page component
 * @param {Node} topEndPoint - *optional - Add additional elements that will be placed above the form component
 * @param {Node} bottomEndPoint - *optional - Add additional elements that will be placed below the form component
 * @param {String} customCSS - *optional - Add or override CSS properties to form container. (SCSS is supported)
 * @param {Object} customSnackBarMessages - *optional - Change default snackbar messages defined in this engine. Keys are: "save_changes_s", "save_changes_f"
 * @param {Function} onFormInitErr - Callback that will be called if one of the init form API calls fails, reason will be passed as parameter tothe function
 * @param {Boolean} showTOC - Pass true if we should render the table of contents on the side
 * @param {Node} requestMoreInfo_TEMP_PROP - Untill we transform request more info to automation we need to build it from the admin side only, this is how we pass that "special" system button props
 * @param {Boolean} viewOnlyMode - if true, no field can be modified
 * Most functions in this component don't need to be wrapped in useCallback due to single render approach taken here
 */
const FormEngine = forwardRef(
  (
    {
      formId,
      submissionId: givenSubmissionId,
      config: _config = {},
      setIsDirty = isDirty => isDirty,
      onDataLoaded = data => data,
      onSubmit = () => {},
      onBlur = () => {},
      onPrevOrNext = () => {},
      onSave = () => {},
      exportForDocusign = () => {},
      beforeSaveChanges = () => {},
      noSubmit,
      noSaveBtn,
      variant = Variants.OT_CLASSIC,
      hideFormTitle,
      hideFormDescription,
      thankUCmp,
      topEndPoint,
      bottomEndPoint,
      customCSS = '',
      customSnackBarMessages = {},
      onFormInitErr = reason => {},
      showTOC,
      requestMoreInfo_TEMP_PROP,
      viewOnlyMode
    },
    ref
  ) => {
    const [initialFormData, dispatch] = useReducer(
      FormReducer,
      InitalReducerState
    );
    const { openSnackbar } = useContext(FetchCallbacksContext);
    const { setIsDocusigning, setIsOTLoading, fullJWT } = useGlobalVariables();
    const history = useHistory();
    const formRef = useRef();
    const formTOCRef = useRef();
    const autoSaveCooldown = useRef(0);
    const calculateCooldown = useRef(0);
    const validators = React.useRef({});
    const customStatusesByMyField = useRef({});
    const stateRef = useRef({
      collapsedSections: {},
      hiddenFields: [],
      touched: new Set(),
      lastSavedValues: {},
      calculationFields: [],
      fieldsThatAreUsedInScoring: []
    });

    const useLoaderOnSave = useRef(false);

    const config = useMemo(
      () => ({ ...defaultFormConfigurations, ..._config }),
      [_config]
    );
    const apiEndpoints = useMemo(
      () => getApiEndpoints(config.endpointType),
      [config]
    );
    const submissionId = useRef(givenSubmissionId);
    const hasIsViewOnlyHeader = useRef(false);
    const calledPostSubmission = useRef(false);
    const referenceId = useRef(null);
    const isSubmitting = useRef(false); // used to indicate the autosave to ignore calls in queue
    const forceReadOnlyMode = useRef(false);
    const viewOnlyAdminMode = useRef(false);
    const textReplacements = useRef([]);
    const isInSubmitSequence = useRef(false);
    const t = useTranslations();
    const { initTimer, getTime } = useTimer();

    const registerField = useCallback(
      (id, validate) => {
        validators.current[id] = validate;
      },
      [validators]
    );

    const [isSubmitted, setIsSubmitted] = useState(false);
    const [
      isIgnoreValidationWarningDialogOpen,
      setIsIgnoreValidationWarningDialogOpen
    ] = useState(false);

    const [ignoreValidationSubmitButtonId, setIgnoreValidationSubmitButtonId] =
      useState(null);

    const postWarningSaveArgs = useRef([]);
    const [isCredasWarningDialogOpen, setIsCredasWarningDialogOpen] =
      useState(false);
    const [isThirdfortWarningDialogOpen, setIsThirdfortWarningDialogOpen] =
      useState(false);

    const stickyBottomBtns =
      config.endpointType === EndpointTypes.ADMIN ||
      getEndpointType() === EndpointTypes.ADMIN;
    const stickyTopBtns = !stickyBottomBtns;

    const isEndUserEndpoint = getEndpointType() === EndpointTypes.END_USER;

    useEffect(() => {
      submissionId.current = givenSubmissionId;
    }, [givenSubmissionId]);

    useEffect(() => {
      // init

      initTimer();
      setIsOTLoading(true);

      const getForm = !apiEndpoints.getForm
        ? null
        : new Promise((resolve, reject) => {
            fetchAPI(apiEndpoints.getForm(formId))
              .then(resolve)
              .catch(e => {
                if (getJWT()) {
                  history.push('/no-access?err=link');
                }
                reject(e);
              });
          });

      const getRules = !apiEndpoints.getRules
        ? []
        : new Promise((resolve, reject) => {
            fetchAPI(apiEndpoints.getRules(formId))
              .then(response => resolve(response.results))
              .catch(reject);
          });

      let _404Err = false;
      const getUserSubmission =
        !submissionId.current || !apiEndpoints.getUserInput
          ? {}
          : new Promise((resolve, reject) => {
              fetchAPI(
                apiEndpoints.getUserInput(submissionId.current, formId),
                {
                  handleResponse: r => {
                    hasIsViewOnlyHeader.current =
                      !!r.headers.map['is-view-only'];
                    return r && r.headers ? r : '';
                  },

                  handleValidationError: e => {
                    if (e && e.code === '404') {
                      _404Err = true;
                    }

                    reject(e);
                  }
                }
              )
                .then(r =>
                  r
                    .text()
                    .then(text => (text ? JSON.parse(text) : {}))
                    .then(json => {
                      resolve(json);
                    })
                )
                .catch(e => {
                  if (getJWT() && !_404Err) {
                    history.push('/no-access?err=link');
                  } else if (!getJWT()) {
                  } else {
                    openSnackbar(
                      'error',
                      t('snackbars.submissions.submission_deleted'),
                      true
                    );
                    history.push(`/dashboard/${formId}`);
                  }
                  reject(e);
                });
            });

      const getIntegrations = !apiEndpoints.getIntegrations
        ? []
        : new Promise((resolve, reject) => {
            fetchAPI(apiEndpoints.getIntegrations(formId))
              .then(response => resolve(response.results))
              .catch(reject);
          });

      Promise.all([getForm, getRules, getUserSubmission, getIntegrations])
        .then(results => {
          // log(`ms after promises complete: ${getTime()}`, 'Performance');
          const returnedAdminFields = results[0]?.adminFields || [];
          useLoaderOnSave.current =
            results[0].fields.length + returnedAdminFields.length >= 1500;
          setFormState(results[0], results[1], results[2], results[3]);
        })
        .catch(e => {
          log(e);
          onFormInitErr(e);
        })
        .finally(() => {
          if (!useLoaderOnSave.current) {
            setIsOTLoading(false);
          }
        });
    }, []);

    const setFormState = useCallback(
      (form, rules, userSubmission, integrations) => {
        if (!form) {
          return;
        }

        const userInput = Object.assign(
          {},
          userSubmission.endUserInput,
          config.showAdminFields ? userSubmission.adminInput : {}
        );

        // Because we join end user and admin fields we need a way to know which field is admin field to append styling on it
        const adminFields = form.adminFields
          ? form.adminFields.map(f => Object.assign(f, { admin: true }))
          : [];
        // log(`ms after admin fields mapping: ${getTime()}`, 'Performance');

        /*
         * allFields, fields and mappedFields explanation:
         *
         * - allFields here is all the fields that exist for this form, with or without the adminFields depending on the config state. It is used only in this function, to set up the fields and mappedFields variables, and to map the values to show for the subfields on initial render of the form.
         *
         * - fields is allFields without any of the subFields, because we don't want to render or handle them separately as well. This one is used for sorting and rendering the fields, their descriptions, etc.
         *
         * - mappedFields is a mapped version of allFields, used for all sorts of non-rendering things, including looking up values. It gets passed around a lot.
         */

        const allFields = [
          ...form.fields,
          ...(config.showAdminFields ? adminFields : [])
        ].sort((a, b) => {
          if (a.position < b.position) {
            return -1;
          }
          if (a.position > b.position) {
            return 1;
          }
          return 0;
        });
        // log(`ms allFields mapping: ${getTime()}`, 'Performance');

        const fields = allFields.filter(one => {
          if (
            one &&
            one.lookupInfo &&
            one.lookupInfo.subFieldInfo &&
            one.lookupInfo.subFieldInfo.parentFieldId
          ) {
            return false;
          }
          return true;
        });
        // log(`ms fields mapping: ${getTime()}`, 'Performance');

        const mappedFields = allFields.reduce((accumulator, currentValue) => {
          accumulator[currentValue.internalId] = currentValue;
          return accumulator;
        }, {});
        // log(`ms mappedFields mapping: ${getTime()}`, 'Performance');

        allFields.forEach(f => {
          if (
            doesUITypeHaveSubfields(f.uiType) &&
            f.lookupInfo &&
            f.lookupInfo.subFields
          ) {
            f.lookupInfo.subFields.forEach(subF => {
              let otherFormFieldId, matchingLocalFields;
              otherFormFieldId = subF.internalId;
              matchingLocalFields = allFields.filter(otherField => {
                if (
                  otherField &&
                  otherField.lookupInfo &&
                  otherField.lookupInfo.subFieldInfo &&
                  otherField.lookupInfo.subFieldInfo.parentFieldId ===
                    f.internalId &&
                  otherField.lookupInfo.subFieldInfo.externalFormFieldId ===
                    otherFormFieldId
                ) {
                  return true;
                }
                return false;
              });
              if (matchingLocalFields && matchingLocalFields.length) {
                subF.localFormFieldId = matchingLocalFields[0].internalId;
                subF.value = userInput[subF.localFormFieldId] || null;
              }
            });
          }
        });
        // log(`ms lookupup mapping: ${getTime()}`, 'Performance');

        let scoringFormula,
          formulaMatches = [],
          fieldsThatAreUsedInScoring = [];
        fields.forEach(field => {
          // set null values to empty fields
          if (
            !userInput[field.internalId] &&
            typeof userInput[field.internalId] !== 'number'
          ) {
            if (
              Object.keys(userSubmission.endUserInput || {}).length === 0 &&
              !field.admin
            ) {
              userInput[field.internalId] =
                (field.defaultValue &&
                  field.defaultValue.properties &&
                  field.defaultValue.properties.value) ||
                null;
            }
          }

          // Multiple choise values are saved with special parsing. If we're going to have more fields with special parsing we'll need
          // to use a more modular solution.
          const veifyMultiSelectInput = () => {
            const inputArr = (userInput[field.internalId] || '')
              .split('||')
              .filter(v => v !== '');
            const updatedInputs = inputArr.filter(v =>
              field.options.includes(v)
            );

            if (JSON.stringify(inputArr) !== JSON.stringify(updatedInputs)) {
              userInput[field.internalId] = `||${updatedInputs.join('||')}||`;
            }
          };

          // nullify deprecated values in options
          if (field.uiType === 'multiple_choice') {
            veifyMultiSelectInput();
          } else if (
            field.uiType !== 'confirmation' &&
            field.uiType !== 'user_lookup' &&
            field.uiType !== 'currency' &&
            field.options &&
            !!field.options.length &&
            !field.options.includes(userInput[field.internalId])
          ) {
            userInput[field.internalId] = null;
          }

          if (field.uiType === 'score') {
            stateRef.current.calculationFields.push(field.internalId);
            try {
              scoringFormula = field.calculationInfo.formula;
              formulaMatches = scoringFormula
                .match(/"f\d+"/g)
                .map(match => match.replace(/"/g, ''));
            } catch (e) {
              console.error('Error parsing scoring formula', e);
            }
          }
          fieldsThatAreUsedInScoring = [
            ...new Set([...fieldsThatAreUsedInScoring, ...formulaMatches])
          ];
        });
        // log(`ms value parsing mapping: ${getTime()}`, 'Performance');

        stateRef.current.fieldsThatAreUsedInScoring =
          fieldsThatAreUsedInScoring;

        stateRef.current.lastSavedValues = Object.assign({}, userInput);

        const lookupData =
          userSubmission && userSubmission.lookupData
            ? userSubmission.lookupData
            : {};

        initFieldDescriptions(fields, rules);
        // log(`ms after init descriptions mapping: ${getTime()}`, 'Performance');

        const docusignIntegrations =
          integrations &&
          integrations.filter(one => one.integrationType === 'docusign');

        const docusign = docusignIntegrations.length
          ? docusignIntegrations
          : null;

        const thirdfortIntegrations =
          integrations &&
          integrations.filter(one => one.integrationType === 'thirdfort');

        const thirdfort = thirdfortIntegrations.length
          ? thirdfortIntegrations[0]
          : null;

        const worldcheckIntegrations =
          integrations &&
          integrations.filter(one => one.integrationType === 'worldcheck');

        const worldcheck = worldcheckIntegrations.length
          ? worldcheckIntegrations[0]
          : noSaveBtn;

        const credasIntegrations =
          integrations &&
          integrations.filter(one => one.integrationType === 'credas');

        const credas = credasIntegrations.length
          ? credasIntegrations[0]
          : noSaveBtn;

        // log(
        //   `ms after integrations mapping mapping: ${getTime()}`,
        //   'Performance'
        // );

        const btns2Filter = [
          docusign
            ? SYSTEM_BUTTONS_DEFAULT_SCHEMA.SUBMIT.id
            : SYSTEM_BUTTONS_DEFAULT_SCHEMA.SIGN_AND_SUBMIT.id,
          ...((!thirdfort && !worldcheck && !credas) ||
          form.sharingType === FormSharingTypes.PUBLIC
            ? [INTEGRATION_BUTTONS_DEFAULT_SCHEMA.SUBMIT_AND_THIRDFORT.id]
            : [])
        ];

        // log(`ms after buttons mapping: ${getTime()}`, 'Performance');

        /*
          - create a map here of sections
          - don't manage state or call any renders from this file
          - probably use stateRef



        */
        const sectionMap = mapSectionsToFields(fields);
        // log(`ms section mapping: ${getTime()}`, 'Performance');

        if (useLoaderOnSave.current && config.showAdminFields) {
          setIsOTLoading(true);
        }
        dispatch({
          type: 'update',
          payload: {
            form,
            docusign,
            thirdfort,
            credas,
            worldcheck,
            userSubmission,
            title: form.title,
            description: form.instructions,
            sectionMap,
            sharingType: form.sharingType,
            fields: useLoaderOnSave.current
              ? (fields || []).slice(0, 10)
              : fields,
            lookupData,
            mappedFields,
            euFieldsIds: form.fields.map(({ internalId }) => internalId),
            adminFieldsIds: adminFields.map(({ internalId }) => internalId),
            userInput,
            rules,
            buttons: getButtons2Show(
              form.buttons,
              (userSubmission.statusData && userSubmission.statusData.status) ||
                null,
              config.endpointType === EndpointTypes.ADMIN ||
                getEndpointType() === EndpointTypes.ADMIN,
              btns2Filter
            ),
            integrationsList: integrations
          }
        });
        if (useLoaderOnSave.current) {
          setTimeout(() => {
            dispatch({
              type: 'update',
              payload: {
                fields
              }
            });
            setIsOTLoading(false);
          }, 500);
        }
      },
      [config]
    );

    useEffect(() => {
      if (
        JSON.stringify(InitalReducerState) === JSON.stringify(initialFormData)
      ) {
        // log(`ms when init state is not ititiated: ${getTime()}`, 'Performance');

        return;
      }
      // log(`ms when init state ititiated: ${getTime()}`, 'Performance');

      let isLocked, matchingStatusObject, submissionStatus;
      const { userSubmission, form } = initialFormData;
      isLocked = false;
      if (
        !config.showAdminFields &&
        userSubmission &&
        userSubmission.statusData &&
        userSubmission.statusData.status
      ) {
        submissionStatus = userSubmission.statusData.status;
        if (form && form.customStatuses && form.customStatuses.length) {
          matchingStatusObject = form.customStatuses.filter(
            one => one.internalId === submissionStatus
          );
          matchingStatusObject = matchingStatusObject[0] || null;
          if (
            matchingStatusObject &&
            matchingStatusObject.lockRules &&
            matchingStatusObject.lockRules.role === 'end_user'
          ) {
            isLocked = true;
          }
        }
      }

      if (
        (form.archived &&
          !(
            config.endpointType === EndpointTypes.ADMIN ||
            getEndpointType() === EndpointTypes.ADMIN
          )) ||
        isLocked
      ) {
        forceReadOnlyMode.current = true;
      }

      if (
        isViewOnlyAdminOfForm(form.id, fullJWT) &&
        (config.endpointType === EndpointTypes.ADMIN ||
          getEndpointType() === EndpointTypes.ADMIN ||
          hasIsViewOnlyHeader.current)
      ) {
        forceReadOnlyMode.current = true;
        viewOnlyAdminMode.current = true;
      }

      const allFields = [...form.fields];
      if (form.adminFields) {
        allFields.concat(form.adminFields);
      }

      const lookupFieldIds = allFields
        .filter(
          one =>
            one &&
            one.lookupInfo &&
            one.lookupInfo.subFieldInfo &&
            one.lookupInfo.subFieldInfo.parentFieldId
        )
        .map(match => match.lookupInfo.subFieldInfo.parentFieldId);

      let lookupFormIdsByMyField = {};
      const lookupStatusFields = allFields.filter(
        one =>
          one.lookupInfo &&
          one.lookupInfo.subFieldInfo &&
          one.uiType === 'status'
      );

      const lookupFormIds = allFields
        .filter(
          one =>
            lookupFieldIds.includes(one.internalId) && one.uiType === 'lookup'
        ) // must be only form lookups, not user lookups
        .map(match => match.lookupInfo.entityId);
      const uniqueLookupFormIds = [...new Set(lookupFormIds)];
      lookupStatusFields.forEach(one => {
        let myFieldId, lookupFieldMatches;
        myFieldId = one.internalId;
        if (
          one.lookupInfo &&
          one.lookupInfo.subFieldInfo &&
          one.lookupInfo.subFieldInfo.parentFieldId
        ) {
          lookupFieldMatches = allFields.filter(
            secondField =>
              secondField.internalId ===
              one.lookupInfo.subFieldInfo.parentFieldId
          );
          if (
            lookupFieldMatches &&
            lookupFieldMatches.length &&
            lookupFieldMatches[0] &&
            lookupFieldMatches[0].lookupInfo &&
            lookupFieldMatches[0].lookupInfo.entityId
          ) {
            lookupFormIdsByMyField[myFieldId] =
              lookupFieldMatches[0].lookupInfo.entityId;
          }
        }
      });

      let allPromises = uniqueLookupFormIds.map(one =>
        getFormStatusesById(one)
      );
      Promise.all(allPromises)
        .then(responses => {
          let newCustomStatusesByMyField, fid, uniqueFormIdIndex;
          newCustomStatusesByMyField = {};
          lookupStatusFields.forEach(one => {
            fid = lookupFormIdsByMyField[one.internalId];
            uniqueFormIdIndex = uniqueLookupFormIds.indexOf(fid);
            newCustomStatusesByMyField[one.internalId] =
              responses[uniqueFormIdIndex];
          });
          customStatusesByMyField.current = newCustomStatusesByMyField;
          // log(`ms after init state ititiated: ${getTime()}`, 'Performance');
          onDataLoaded(initialFormData, viewOnlyAdminMode.current);
        })
        .catch(e => {
          onDataLoaded(initialFormData, viewOnlyAdminMode.current);
        });
    }, [initialFormData]);

    useLayoutEffect(() => {
      // log(`ms before init hidden fields: ${getTime()}`, 'Performance');

      initHiddenFieldsOnDOM(
        stateRef,
        Object.values(initialFormData.mappedFields),
        initialFormData.userInput,
        initialFormData.rules,
        config
      );
      // log(`ms after init hidden fields on dom: ${getTime()}`, 'Performance');

      formTOCRef.current &&
        formTOCRef.current.initHiddenFields(stateRef.current);
      // log(`ms after init hidden fields: ${getTime()}`, 'Performance');
      initFieldDescriptionsOnDOM(
        Object.values(initialFormData.mappedFields),
        initialFormData.userInput,
        initialFormData.rules
      );
      // log(`ms after init description fields: ${getTime()}`, 'Performance');
      setTimeout(() => {
        // We need to catchlook up fields sub fields. Because lookup fields
        // are resopnisble of rendering their own sub fields we have to let their
        // render cycles finish and load them on to the DOM before we run those functions
        initTextReplacements(textReplacements, formRef);
        // log(`ms after init text replacments: ${getTime()}`, 'Performance');

        updateTextReplacements(
          textReplacements,
          null,
          formRef,
          customStatusesByMyField
        );
        // log(`ms after update text replacments: ${getTime()}`, 'Performance');
      }, 500);
    }, [initialFormData]);

    const getValues = useCallback(
      ignoreHidden => {
        const values = {};
        const form = formRef.current;
        if (!form) {
          return;
        }
        const fields = form.querySelectorAll('[field-id]');
        fields.forEach(fNode => {
          const fieldId = fNode.getAttribute('field-id');
          if (ignoreHidden && stateRef.current.hiddenFields.includes(fieldId)) {
            values[fieldId] = null;
            return;
          }
          let value = fNode.getAttribute('value');
          const fieldType = initialFormData.mappedFields[fieldId].type;
          if (fieldType === 'number' || fieldType === 'date') {
            value = isNaN(parseFloat(value)) ? null : parseFloat(value);
          }
          values[fieldId] = value;
        });

        if (!config.isReadOnlyFieldsEditable) {
          // Make sure read only fields were not manipulated
          initialFormData.fields.forEach(field => {
            if (field.readOnly) {
              values[field.internalId] =
                initialFormData.userInput[field.internalId] || null;
            }
          });
        }

        return values;
      },
      [formRef.current, initialFormData, config]
    );

    const isInAnimationSequance = useRef(false);
    const logicCallTS = useRef(0);

    const onFormChange = useCallback(
      (
        blockSave,
        skipCalculationFieldsCallback,
        valuesAboutToUpdate,
        internalId = '' // we have to allow calls without an internalId, and treat it as calculation-necessary to handle situations where onFormChange is called without params
      ) => {
        let keysToCheckIfTheyreInvolvedInCalculation = [];
        if (internalId) {
          keysToCheckIfTheyreInvolvedInCalculation.push(internalId);
        }
        if (valuesAboutToUpdate) {
          keysToCheckIfTheyreInvolvedInCalculation = [
            ...keysToCheckIfTheyreInvolvedInCalculation,
            ...Object.keys(valuesAboutToUpdate)
          ];
        }
        console.log('top blockSave', blockSave);
        console.log(
          'top skipCalculationFieldsCallback',
          skipCalculationFieldsCallback
        );

        updateTextReplacements(
          textReplacements,
          null,
          formRef,
          customStatusesByMyField
        );
        if (
          config.useAutoSave &&
          initialFormData.sharingType !== FormSharingTypes.PUBLIC &&
          !blockSave
        ) {
          saveProgress();
        }

        /* update calculation fields if:
         * - there is at least one calculation field AND
         * - we didn't call this with skipCalculationFieldsCallback AND
         * - either
         *    - the internalId or one of the valuesAboutToUpdate is used in scoring OR
         *    - neither an internalId nor a valuesAboutToUpdate was passed through so we don't know specifically what happened
         */
        if (
          stateRef.current.calculationFields.length &&
          !skipCalculationFieldsCallback &&
          (!keysToCheckIfTheyreInvolvedInCalculation.length ||
            stateRef.current.fieldsThatAreUsedInScoring.some(val =>
              keysToCheckIfTheyreInvolvedInCalculation.includes(val)
            ))
        ) {
          let body = getSubmitBody();
          if (valuesAboutToUpdate) {
            const fieldIdsAboutToUpdate = Object.keys(valuesAboutToUpdate);
            fieldIdsAboutToUpdate.forEach(oneKey => {
              if (
                initialFormData.euFieldsIds.includes(oneKey) &&
                body.endUserInput
              ) {
                body.endUserInput[oneKey] = valuesAboutToUpdate[oneKey];
              } else if (body.adminInput) {
                body.adminInput[oneKey] = valuesAboutToUpdate[oneKey];
              }
            });
          }

          calculateCooldown.current++;

          setTimeout(() => {
            calculateCooldown.current--;
            if (calculateCooldown.current === 0 && !isSubmitting.current) {
              fetchAPI(apiEndpoints.calculate(formId), {
                method: 'PUT',
                body
              }).then(res => {
                const results =
                  res && res.results && res.results.length
                    ? res.results[0]
                    : {};
                updateCalculationFields(results);
              });
            }
          }, CALCULATION_DELAY_THRESHOLD);
        }

        const updateLogicElements = (ts, fields, values, rules, stateRef) => {
          if (logicCallTS.current > ts) {
            // New call got in - discard current one which is older
            return;
          }
          logicCallTS.current = ts;

          if (isInAnimationSequance.current) {
            // Animating... try again soon!
            setTimeout(() => {
              updateLogicElements(ts, fields, values, rules, stateRef);
            }, 100);
            return;
          }

          let allSectionIds,
            divsToCollapse = [];

          allSectionIds = Object.keys(initialFormData.sectionMap);
          allSectionIds.forEach(id => {
            if (id && initialFormData.sectionMap[id]) {
              if (stateRef.current.collapsedSections[id]) {
                divsToCollapse = [
                  ...divsToCollapse,
                  ...initialFormData.sectionMap[id]
                ];
              }
            }
          });

          const forceHidden = divsToCollapse;
          // All conditions cleared! May perform logic evaluation and execution
          isInAnimationSequance.current = true;
          updateHiddenFieldsOnDOM(
            stateRef,
            fields,
            values,
            rules,
            config,
            forceHidden,
            () => {
              isInAnimationSequance.current = false;
            }
          );
          updateDescriptionsOnDOM(fields, values, rules);
          // Note that the show hide animation duration is longer therfore the callback is located
          // there (updateHiddenFieldsOnDOM). Not the most flexible way therfore be aware of changes
          // to the animations durations!
          formTOCRef.current &&
            formTOCRef.current.updateHiddenFields &&
            formTOCRef.current.updateHiddenFields(stateRef.current);
        };

        let prevValues,
          currentValues,
          loopCounter = 0;
        const MAX_LOOPS = 10;
        do {
          prevValues = getValues(true);
          updateLogicElements(
            Date.now(),
            Object.values(initialFormData.mappedFields),
            prevValues,
            initialFormData.rules,
            stateRef
          );
          currentValues = getValues(true);
          loopCounter++;
        } while (
          JSON.stringify(currentValues) !== JSON.stringify(prevValues) &&
          loopCounter < MAX_LOOPS
        );
      },
      [
        initialFormData,
        config,
        updateHiddenFieldsOnDOM,
        updateDescriptionsOnDOM,
        getValues,
        stateRef.current,
        formTOCRef.current
      ]
    );

    const onFormBlur = e => {
      if (forceReadOnlyMode.current || config.isFormReadOnly) {
        return;
      }

      const handleIsDirty = targetNode => {
        const fieldId = targetNode.getAttribute('field-id');
        let currentValue = (targetNode.getAttribute('value') || '').trim();
        let initialFieldValue = stateRef.current.lastSavedValues[fieldId];
        let localSubFieldIds, subFieldNode, subFieldValue;
        localSubFieldIds = [];
        if (typeof initialFieldValue === 'string') {
          initialFieldValue = initialFieldValue.trim();
        }
        if (initialFieldValue === null) {
          initialFieldValue = '';
        }
        if (initialFormData.mappedFields[fieldId].type === 'number') {
          currentValue = parseFloat(currentValue) || null;
        }
        const fieldWasTouchedBefore = stateRef.current.touched.has(fieldId);

        if (fieldWasTouchedBefore && currentValue === initialFieldValue) {
          // remove
          stateRef.current.touched.delete(fieldId);
        } else if (
          !fieldWasTouchedBefore &&
          currentValue !== initialFieldValue &&
          (currentValue || initialFieldValue)
        ) {
          // add
          stateRef.current.touched.add(fieldId);
        }

        if (
          initialFormData.mappedFields[fieldId].lookupInfo &&
          initialFormData.mappedFields[fieldId].lookupInfo.subFields
        ) {
          localSubFieldIds = initialFormData.mappedFields[
            fieldId
          ].lookupInfo.subFields.map(one => one.localFormFieldId);
          localSubFieldIds.forEach(subFieldId => {
            subFieldNode = targetNode.querySelector(
              `[field-id="${subFieldId}"]`
            );
            if (subFieldNode) {
              subFieldValue = (subFieldNode.getAttribute('value') || '').trim();
              if (initialFormData.mappedFields[subFieldId].type === 'number') {
                subFieldValue = parseFloat(subFieldValue) || null;
              }
              if (
                subFieldValue !==
                  stateRef.current.lastSavedValues[subFieldId] &&
                (subFieldValue || stateRef.current.lastSavedValues[subFieldId])
              ) {
                stateRef.current.touched.add(subFieldId);
              }
            }
          });
        }

        setIsDirty(stateRef.current.touched.size > 0);
      };

      const node = e.target.closest('[field-id]');

      if (node) {
        // blur can be triggered by submit ot other button in form scope
        onBlur();
        handleIsDirty(node);
      }
    };

    /**
     * Make sure all fields are still in the DOM and that no one messed with it
     *
     * @param {*} domScope
     * @param {*} listOfFieldsIds
     */
    const validateDOMIntegrity = () => {
      const form = formRef.current;
      const listOfFieldsIds = Object.keys(initialFormData.mappedFields);

      for (let i = 0; i < listOfFieldsIds.length; i++) {
        const id = listOfFieldsIds[i];
        if (
          !form.querySelector(`[field-id="${id}"]`) &&
          initialFormData.mappedFields[id] &&
          !initialFormData.mappedFields[id].archived
        ) {
          log('DOM was manipulated!');
          openSnackbar(
            'error',
            t('snackbars.submissions.submission_error.DOM_manipulation')
          );
          return false;
        }
      }

      return true;
    };

    const onFormValidation = (values, overrideConfigToValidate = false) => {
      let isFormValid = true;

      Object.keys(initialFormData.mappedFields).forEach(fieldId => {
        if (
          !validateField(fieldId, values[fieldId], overrideConfigToValidate)
        ) {
          isFormValid = false;
        }
      });

      return isFormValid;
    };

    const onWorldCheckFormValidation = (e, values) => {
      let hasInvalidWorldCheckField = false;

      const worldCheckFields = Object.values(
        initialFormData.mappedFields
      ).filter(
        one => one.uiType === 'worldcheck' || one.uiType === 'worldcheck_org'
      );

      let allWorldCheckChildFieldIds = [];

      worldCheckFields.forEach(parentField => {
        const childIds = parentField.lookupInfo.subFields.map(
          one => one.localFormFieldId
        );
        allWorldCheckChildFieldIds = [
          ...allWorldCheckChildFieldIds,
          ...childIds
        ];
      });

      allWorldCheckChildFieldIds.forEach(fieldId => {
        if (!validateField(fieldId, values[fieldId], true)) {
          hasInvalidWorldCheckField = true;
        }
      });

      if (hasInvalidWorldCheckField) {
        scroll2FirstError();
        e.preventDefault();
      } else {
        onFormSubmit(e);
      }
    };

    /**
     *
     * @param {String} fieldId - The id of the field that we want to validate
     * @param {String} fieldValue - Optional - Pass value to validate the field against. If empty will look for the value in the DOM
     * @param {Boolean} overrideConfigToValidate - Optional - if true, require validation regardless of config
     */
    const validateField = useCallback(
      (fieldId, fieldValue, overrideConfigToValidate) => {
        if (
          forceReadOnlyMode.current ||
          config.isFormReadOnly ||
          !validators.current[fieldId] ||
          stateRef.current.hiddenFields.includes(fieldId)
        ) {
          return true;
        }
        const form = formRef.current;
        const fieldNode = form.querySelector(`[field-id="${fieldId}"]`);
        const errorP = fieldNode.querySelector('[data-error]');

        if (fieldValue === undefined) {
          fieldValue = fieldNode.getAttribute('value');
        }
        const onFieldValidate = validators.current[fieldId];
        const error = onFieldValidate(fieldValue);
        const fieldTitle =
          initialFormData.mappedFields[fieldId].title || fieldId;
        if (error) {
          if (config.useValidation || overrideConfigToValidate) {
            // set element class to error
            fieldNode.classList.add('EUF-error');
            if (errorP?.textContent) {
              errorP.textContent = error || '\xa0';
            } else {
              openSnackbar('error', 'Error validating field ' + fieldTitle);
            }
          }
          return false;
        }
        // remove element error class in case there was one before
        fieldNode.classList.remove('EUF-error');
        if (errorP?.textContent) {
          errorP.textContent = '';
        }
        return true;
      },
      [initialFormData, validators, stateRef, formRef]
    );

    /**
     * Will return the existing submission id or create a new submissions and will return its id
     */
    const getSubmissionsId = useCallback(
      (createSubmission = true) =>
        new Promise((resolve, reject) => {
          if (submissionId.current || !createSubmission) {
            resolve(submissionId.current);
            return;
          }
          if (calledPostSubmission.current) {
            reject('Trying to create an extra submission');
            return;
          }
          calledPostSubmission.current = true;
          if (!apiEndpoints.postSubmission) {
            reject('Invalid POST endpoint');
          }
          fetchAPI(apiEndpoints.postSubmission(formId), {
            method: 'POST',
            body: {
              adminInput: {},
              endUserInput: {},
              formId
            },
            handleResponse: r => {
              submissionId.current = r.headers.map.location;
              referenceId.current = r.headers.map['x-vinci-reference-id'];
              resolve(r.headers.map.location);
            }
          }).catch(e => {
            calledPostSubmission.current = false;
            reject(e);
          });
        }),
      [apiEndpoints]
    );

    const getReferenceId = useCallback(
      () =>
        new Promise((resolve, reject) => {
          if (referenceId.current) {
            resolve(referenceId.current);
          } else if (
            initialFormData &&
            initialFormData.userSubmission &&
            initialFormData.userSubmission.referenceId
          ) {
            resolve(initialFormData.userSubmission.referenceId);
          } else {
            resolve(null);
          }
        }),
      [initialFormData, referenceId.current]
    );

    const scroll2FirstError = () => {
      const form = formRef.current;
      const firstError = form.querySelector('.EUF-error');
      gsap.to('section', {
        scrollTo: {
          y: firstError,
          offsetY: Sizes.NAV_BAR_HEIGHT + (stickyTopBtns ? 53 : 0)
        }
      });
      gsap.to(`div[class*="MuiDrawer-paper"]`, {
        scrollTo: { y: firstError, offsetY: 64 + (stickyTopBtns ? 53 : 0) }
      });
    };

    const onFormSubmit = e => {
      e.preventDefault();
      const submitterButton =
        (e && e.nativeEvent && e.nativeEvent.submitter) || undefined;

      const buttonId =
        (e && e.currentTarget && e.currentTarget.id) ||
        (submitterButton && submitterButton.id) ||
        undefined;

      const form = formRef.current;
      const values = getValues(true);

      if (!validateDOMIntegrity()) {
        return;
      }
      if (!onFormValidation(values)) {
        if (!config.useValidation) {
          setIgnoreValidationSubmitButtonId(buttonId);
          setIsIgnoreValidationWarningDialogOpen(true);
          return;
        }

        scroll2FirstError();
        return;
      }

      submitForm(values, buttonId);
    };

    const getLookupFieldData = useCallback(() => {
      let result, oneResult, hasLookupFields;
      result = [];
      hasLookupFields = false;
      if (
        initialFormData.lookupData &&
        initialFormData.lookupData.lookupFields
      ) {
        result = initialFormData.lookupData.lookupFields;
      }
      try {
        const form = formRef.current;
        const lookupFields = Object.values(initialFormData.mappedFields).filter(
          one => one.uiType === 'lookup' || one.uiType === 'user_lookup'
        );
        const listOfFieldsIds = lookupFields.map(one => one.internalId);

        if (!listOfFieldsIds.length) {
          return null;
        }

        for (let i = 0; i < listOfFieldsIds.length; i++) {
          let submissionId, divWithSubmissionId, userId, divWithUserId;
          submissionId = null;

          const localFieldId = listOfFieldsIds[i];
          const lookupInfo =
            initialFormData.mappedFields[localFieldId].lookupInfo;
          const field = form.querySelector(`[field-id="${localFieldId}"]`);

          if (field && field.firstElementChild) {
            divWithSubmissionId = field.querySelector('[submission-id]');
            if (divWithSubmissionId) {
              submissionId = divWithSubmissionId.getAttribute('submission-id');
            }
            divWithUserId = field.querySelector('[user-id]');
            if (divWithUserId) {
              userId = divWithUserId.getAttribute('user-id');
            }
            if (submissionId) {
              oneResult = {
                formId: lookupInfo.entityId,
                fieldId: lookupInfo.keyField,
                submissionId: submissionId,
                localFieldId: localFieldId
              };
              result = result.filter(
                one =>
                  (one.userId ||
                    one.formId !== lookupInfo.entityId ||
                    one.fieldId !== lookupInfo.keyField ||
                    one.localFieldId !== localFieldId) &&
                  (!one.localFieldId || one.localFieldId !== localFieldId)
              );
              result.push(oneResult);
            } else if (userId) {
              oneResult = {
                userId: userId,
                localFieldId: localFieldId
              };
              result = result.filter(
                one =>
                  (one.userId !== lookupInfo.userId ||
                    one.formId ||
                    one.localFieldId !== localFieldId) &&
                  (!one.localFieldId || one.localFieldId !== localFieldId)
              );
              result.push(oneResult);
            } else if (localFieldId) {
              result = result.filter(one => {
                return one.localFieldId !== localFieldId;
              });
            }
          }
        }
      } catch (e) {
        log('caught error in getLookupFieldData(): ', e);
      }
      return result;
    }, [formRef, initialFormData]);

    const getSubmitBody = values => {
      let body, lookupFieldData;

      try {
        lookupFieldData = getLookupFieldData();
        if (!values) {
          values = getValues(true);
        }

        body = {
          formId,
          ...seperateAdminFromEuFields(
            initialFormData.adminFieldsIds,
            initialFormData.euFieldsIds,
            values
          ),
          ignoredFields: [
            ...stateRef.current.hiddenFields,
            ...initialFormData.fields
              .map(f => (f.archived ? f.internalId : null))
              .filter(id => id !== null)
          ]
        };

        if (lookupFieldData !== null) {
          body.lookupData = {
            lookupFields: lookupFieldData
          };
        }
        return body;
      } catch (e) {
        log(e);
        openSnackbar('error', t('snackbars.submissions.submission_error'));
      }
    };

    const getButtonName = (ButtonId, form) => {
      return !!ButtonId
        ? form?.buttons?.customButtons.find(btn => btn.id === ButtonId)?.label
        : '';
    };

    const submitForm = (_values, buttonId) => {
      if (isInSubmitSequence.current) {
        return;
      }
      isInSubmitSequence.current = true;

      const values = _values ? _values : getValues();

      stateRef.current.hiddenFields.forEach(fId => (values[fId] = null));
      isSubmitting.current = true;
      setIsOTLoading(true);

      const unchangedFields = getFieldsThatShouldBeUnchanged();

      const adminFieldsToGetFromDB = unchangedFields?.adminFieldIds || [];
      const fieldsToGetFromDB = unchangedFields?.endUserFieldIds || [];

      getSubmissionsId()
        .then(sId => {
          const body = getSubmitBody(values);
          if (buttonId) {
            if (
              buttonId === 'SUBMIT_AND_THIRDFORT' &&
              initialFormData &&
              (initialFormData.thirdfort ||
                initialFormData.worldcheck ||
                initialFormData.credas)
            ) {
              if (
                buttonId === 'SUBMIT_AND_THIRDFORT' &&
                initialFormData?.credas
              ) {
                let cantRunCredas = false;
                const credasCheckAdminRequiredFields =
                  initialFormData.form.fields.filter(one => {
                    return CREDAS_CHECK_ADMIN_REQUIRED_FIELDS.includes(
                      one?.lookupInfo?.subFieldInfo?.externalFormFieldId
                    );
                  });
                const requiredFieldIds = credasCheckAdminRequiredFields.map(
                  one => one.internalId
                );
                requiredFieldIds.forEach(oneFieldId => {
                  if (
                    !stateRef.current.hiddenFields.includes(oneFieldId) &&
                    !values[oneFieldId]
                  ) {
                    openSnackbar(
                      'error',
                      t('snackbars.submissions.credas_submission_error')
                    );
                    cantRunCredas = true;
                  }
                });
                const credasCheckTypeFields =
                  initialFormData.form.fields.filter(
                    one =>
                      one?.lookupInfo?.subFieldInfo?.externalFormFieldId ===
                      'checktype'
                  );
                credasCheckTypeFields.forEach(one => {
                  const checktypeFieldId = one.internalId;
                  const checktypeValue = values[checktypeFieldId];
                  if (checktypeValue === 'Full ID verification') {
                    const parentId =
                      one?.lookupInfo?.subFieldInfo?.parentFieldId;
                    const credasFullIDCheckAdminRequiredFields =
                      initialFormData.form.fields.filter(
                        anotherOne =>
                          CREDAS_FULL_ID_CHECK_ADMIN_REQUIRED_FIELDS.includes(
                            anotherOne?.lookupInfo?.subFieldInfo
                              ?.externalFormFieldId
                          ) &&
                          anotherOne.lookupInfo.subFieldInfo.parentFieldId ===
                            parentId
                      );
                    const credasFullIDCheckAdminRequiredFieldIds =
                      credasFullIDCheckAdminRequiredFields.map(
                        one => one.internalId
                      );
                    credasFullIDCheckAdminRequiredFieldIds.forEach(
                      oneFieldId => {
                        if (!values[oneFieldId]) {
                          openSnackbar(
                            'error',
                            t(
                              'snackbars.submissions.credas_submission_error_two'
                            )
                          );
                          cantRunCredas = true;
                        }
                      }
                    );
                  }
                });

                if (cantRunCredas) {
                  return false;
                }
              }
              body.requestInfo = {
                integrationInfo: [],
                adminFieldsToGetFromDB,
                fieldsToGetFromDB
              };
              if (initialFormData.thirdfort) {
                body.requestInfo.integrationInfo.push({
                  integrationType: 'thirdfort',
                  integrationId: initialFormData.thirdfort.id
                });
              }
              if (initialFormData.worldcheck) {
                body.requestInfo.integrationInfo.push({
                  integrationType: 'worldcheck',
                  integrationId: initialFormData.worldcheck.id
                });
              }
              if (initialFormData.credas) {
                body.requestInfo.integrationInfo.push({
                  integrationType: 'credas',
                  integrationId: initialFormData.credas.id
                });
              }
            } else {
              body.requestInfo = {
                buttonId: buttonId,
                buttonName: !!getButtonName(buttonId, initialFormData)
                  ? getButtonName(buttonId, initialFormData)
                  : undefined,
                adminFieldsToGetFromDB,
                fieldsToGetFromDB
              };
            }
          }
          if (body && !body.requestInfo) {
            body.requestInfo = {
              adminFieldsToGetFromDB,
              fieldsToGetFromDB
            };
          }
          fetchAPI(apiEndpoints.submit(sId, formId), {
            method: 'PUT',
            body
          }).then(() => {
            setIsSubmitted(true);
            stateRef.current.touched.clear();
            onSubmit();
          });
        })
        .catch(e => {
          log(e);
          openSnackbar('error', t('snackbars.submissions.submission_error'));
        })
        .finally(() => {
          setIsOTLoading(false);
          isInSubmitSequence.current = false;
        });
    };

    const updateCalculationFields = userInputs => {
      stateRef.current.calculationFields.forEach(cFieldId => {
        const newValue = userInputs[cFieldId];
        const formattedNewValue = newValue
          ? newValue.replace(/\.0$/, '')
          : newValue;
        const calcFieldSpan =
          formRef.current &&
          formRef.current.querySelector(`[data-calc-id="${cFieldId}"] span`);
        const calcField =
          formRef.current &&
          formRef.current.querySelector(`[field-id="${cFieldId}"]`);
        if (calcField && calcFieldSpan) {
          if (newValue !== null) {
            calcFieldSpan.classList.remove('error');
            calcFieldSpan.innerText = formatIfNumber(formattedNewValue);
          } else {
            calcFieldSpan.classList.add('error');
            calcFieldSpan.innerText = `${t('forms.fields.score_error')}`;
          }

          calcField.setAttribute('value', formattedNewValue);
        }
        onFormChange(true, true);
      });
    };

    const checkIfWarning = (e, showSnackbar, callback, onSaveExtras) => {
      const mappedFields = initialFormData.mappedFields;
      const fieldIds = Object.keys(mappedFields);
      const hasCredasWithExistingValue = fieldIds.some(id => {
        const oneField = mappedFields[id];
        let match, subFieldInfo;
        if (
          oneField.uiType === 'credas' ||
          oneField.uiType === 'credas_out_of_scope'
        ) {
          subFieldInfo = oneField.lookupInfo?.subFields || [];
          match = subFieldInfo.find(one => one.internalId === 'status');
          if (match && match.value) {
            return true;
          }
        }
      });
      const hasThirdfortWithExistingValue = fieldIds.some(id => {
        const oneField = mappedFields[id];
        let match, subFieldInfo;
        if (oneField.uiType === 'thirdfort') {
          subFieldInfo = oneField.lookupInfo?.subFields || [];
          match = subFieldInfo.find(one => one.internalId === 'status');
          if (match && match.value) {
            return true;
          }
        }
      });
      if (hasCredasWithExistingValue && !isEndUserEndpoint) {
        postWarningSaveArgs.current = [
          e,
          showSnackbar,
          callback,
          onSaveExtras,
          true,
          e.currentTarget.id
        ];
        setIsCredasWarningDialogOpen(true);
        return true;
      } else if (hasThirdfortWithExistingValue && !isEndUserEndpoint) {
        postWarningSaveArgs.current = [
          e,
          showSnackbar,
          callback,
          onSaveExtras,
          true,
          e.currentTarget.id
        ];
        setIsThirdfortWarningDialogOpen(true);
        return true;
      } else {
        return false;
      }
    };

    const saveWithoutWarning = () => {
      const saveArgs = postWarningSaveArgs.current;
      saveProgress(
        saveArgs[0],
        saveArgs[1],
        saveArgs[2],
        saveArgs[3],
        saveArgs[4],
        saveArgs[5]
      );
    };

    const getFieldsThatShouldBeUnchanged = useCallback(() => {
      try {
        const PARENT_UITYPES = [
          'credas',
          'thirdfort',
          'worldcheck',
          'worldcheck_org'
        ];
        const GENERIC_SUBFIELDS = ['status', 'date'];
        const CREDAS_SUBFIELDS = [
          'status',
          'date',
          'addressMatch',
          'loeStatus',
          'title',
          'contactVia',
          'runAgain',
          'loe',
          'checktype'
        ];
        let endUserFieldIds = [],
          adminFieldIds = [],
          parentFieldIds = [],
          WHICH_SUBFIELDS = GENERIC_SUBFIELDS;
        const allEndUserFields = initialFormData.form.fields;
        const allAdminFields = initialFormData.form.adminFields || [];
        // need to handle worldcheck_org and others also
        parentFieldIds = [
          ...allEndUserFields
            .filter(one => PARENT_UITYPES.includes(one.uiType))
            .map(one => one.internalId),
          ...allAdminFields
            .filter(one => PARENT_UITYPES.includes(one.uiType))
            .map(one => one.internalId)
        ];
        if (
          [
            ...allEndUserFields
              .filter(one => one.uiType === 'credas')
              .map(one => one.internalId),
            ...allAdminFields
              .filter(one => one.uiType === 'credas')
              .map(one => one.internalId)
          ].length
        ) {
          WHICH_SUBFIELDS = CREDAS_SUBFIELDS;
        }
        endUserFieldIds = allEndUserFields
          .filter(
            one =>
              parentFieldIds.includes(
                one.lookupInfo?.subFieldInfo?.parentFieldId
              ) &&
              WHICH_SUBFIELDS.includes(
                one.lookupInfo?.subFieldInfo?.externalFormFieldId
              )
          )
          .map(one => one.internalId);

        adminFieldIds = allAdminFields
          .filter(
            one =>
              parentFieldIds.includes(
                one.lookupInfo?.subFieldInfo?.parentFieldId
              ) &&
              WHICH_SUBFIELDS.includes(
                one.lookupInfo?.subFieldInfo?.externalFormFieldId
              )
          )
          .map(one => one.internalId);
        return {
          endUserFieldIds,
          adminFieldIds
        };
      } catch (e) {
        console.error(e);
        return { endUserFieldIds: [], adminFieldIds: [] };
      }
    }, [initialFormData.form]);

    const saveProgress = useCallback(
      (
        e,
        showSnackbar,
        callback,
        onSaveExtras,
        withoutWarning,
        savedButtonId
      ) => {
        if (
          !withoutWarning &&
          checkIfWarning(e, showSnackbar, callback, onSaveExtras)
        ) {
          return;
        }
        const buttonId =
          savedButtonId ||
          (e && e.currentTarget && e.currentTarget.id) ||
          undefined;
        const saveIndicatorId = buttonId || 'save-changes-indicator';
        const isCallMadeBySystemButton = !!Object.values(
          SYSTEM_BUTTONS_DEFAULT_SCHEMA
        ).find(sb => sb.id === buttonId);

        let lookupFieldData = getLookupFieldData();
        autoSaveCooldown.current++;
        setSaveChangesState(States.SAVING, `[data-id="${saveIndicatorId}"]`);

        const unchangedFields = getFieldsThatShouldBeUnchanged();

        const adminFieldsToGetFromDB = unchangedFields?.adminFieldIds || [];
        const fieldsToGetFromDB = unchangedFields?.endUserFieldIds || [];

        const onSaveProgress = () => {
          if (!apiEndpoints.saveSubmission) {
            return;
          }
          const values = getValues(true);
          getSubmissionsId().then(sId => {
            const endPoint = apiEndpoints.saveSubmission(sId, formId);
            let body = {
              requestInfo: {
                buttonId: isCallMadeBySystemButton ? undefined : buttonId,
                buttonName: !!getButtonName(buttonId, initialFormData)
                  ? getButtonName(buttonId, initialFormData)
                  : undefined,
                adminFieldsToGetFromDB,
                fieldsToGetFromDB
              },
              formId,
              ...seperateAdminFromEuFields(
                initialFormData.adminFieldsIds,
                initialFormData.euFieldsIds,
                values
              )
            };
            if (lookupFieldData !== null) {
              body.lookupData = {
                lookupFields: lookupFieldData
              };
            }

            fetchAPI(endPoint, {
              method: 'PUT',
              body: body
            })
              .then(() => {
                setSaveChangesState(
                  States.SAVED,
                  `[data-id="${saveIndicatorId}"]`
                );
                stateRef.current.lastSavedValues = values;
                stateRef.current.touched.clear();
                setIsDirty(false);
                onSave(sId, onSaveExtras);
                if (callback) {
                  callback();
                }

                if (showSnackbar) {
                  openSnackbar(
                    'success',
                    customSnackBarMessages.save_changes_s ||
                      'snackbars.autosave.regular'
                  );
                }
              })
              .catch(() => {
                setSaveChangesState(
                  States.FAILED,
                  `[data-id="${saveIndicatorId}"]`
                );
                if (showSnackbar) {
                  openSnackbar(
                    'error',
                    customSnackBarMessages.save_changes_f ||
                      'snackbars.changeSharing.failure'
                  );
                }
              });
          });
        };

        if (useLoaderOnSave.current && config.showAdminFields) {
          setIsOTLoading(true);
        }
        setTimeout(async () => {
          autoSaveCooldown.current--;
          if (autoSaveCooldown.current === 0 && !isSubmitting.current) {
            await beforeSaveChanges();
            onSaveProgress();
          }
        }, AUTOSAVE_DELAY_THRESHOLD);
      },
      [
        config,
        getLookupFieldData,
        apiEndpoints,
        getValues,
        getSubmissionsId,
        setIsDirty,
        onSave,
        beforeSaveChanges,
        initialFormData.form
      ]
    );

    const prevOrNext = next => {
      onPrevOrNext(next);
    };

    const onThirdfortSubmit = e => {
      const values = getValues(true);

      if (!validateDOMIntegrity()) {
        return;
      }
      if (!onFormValidation(values, true)) {
        // we require validation on both admin and end user for docusign

        scroll2FirstError();
        return;
      }
      onFormSubmit(e);
    };

    const onWorldCheckSubmit = e => {
      const values = getValues(true);

      if (!validateDOMIntegrity()) {
        return;
      }
      // for worldcheck submit, always need to validate worldcheck subfields, but only need validation of other fields for end users
      // and warning for admins
      onWorldCheckFormValidation(e, values);
    };

    const onDocusignSubmit = () => {
      const values = getValues(true);

      if (!validateDOMIntegrity()) {
        return;
      }
      if (!onFormValidation(values, true)) {
        // we require validation on both admin and end user for docusign

        scroll2FirstError();
        return;
      }

      setIsDocusigning(true);
      saveProgress(null, false, () => {
        const getIntegrations = !apiEndpoints.getIntegrations
          ? []
          : new Promise((resolve, reject) => {
              fetchAPI(apiEndpoints.getIntegrations(formId))
                .then(response => resolve(response.results))
                .catch(reject);
            });
        Promise.all([getSubmissionsId(), getIntegrations]).then(results => {
          let sId,
            integrations,
            docusignIntegrations,
            firstDocusignIntegration,
            integrationId,
            template,
            includeBlanks,
            includeLogo;
          sId = results[0];
          integrations = results[1];
          docusignIntegrations = integrations.filter(
            one => one.integrationType === 'docusign'
          );
          firstDocusignIntegration =
            docusignIntegrations && !!docusignIntegrations.length
              ? docusignIntegrations[0]
              : {};
          integrationId = firstDocusignIntegration.id || '';
          template =
            firstDocusignIntegration.additionalParams &&
            firstDocusignIntegration.additionalParams.reportData &&
            firstDocusignIntegration.additionalParams.reportData.template
              ? firstDocusignIntegration.additionalParams.reportData.template
              : 'full-submission';
          if (!firstDocusignIntegration.id) {
            firstDocusignIntegration.id = '';
          }
          if (
            firstDocusignIntegration.additionalParams.reportData.includeBlanks
          ) {
            includeBlanks = true;
          } else {
            includeBlanks = false;
          }
          if (
            firstDocusignIntegration.additionalParams.reportData.includeLogo ||
            typeof firstDocusignIntegration.additionalParams.reportData
              .includeLogo === 'undefined'
          ) {
            includeLogo = true;
          } else {
            includeLogo = false;
          }
          exportForDocusign({
            createPDFForDocusign: true,
            exportFormId: formId,
            exportSubmissionId: sId,
            exportReport: template,
            exportIncludeBlanks: includeBlanks,
            exportIncludeLogo: includeLogo,
            noDownload: true,
            docusignIntegrationId: integrationId
          });
        });
      });
    };

    log('Form engine component rendered!');

    useImperativeHandle(
      ref,
      () => ({
        getSubmissionsId: getSubmissionsId,
        getReferenceId: getReferenceId,
        getForm: () => initialFormData.form,
        getSubmitBody: getSubmitBody,
        getUserSubmission: () => initialFormData.userSubmission,
        showThankYou: () => setIsSubmitted(true),
        saveProgress: saveProgress
      }),
      [getSubmissionsId, getReferenceId, initialFormData, saveProgress]
    );

    const getCustomButtonOperation = useCallback(
      buttonData => {
        const buttonType = buttonData.type;

        if (buttonType === 'admin' || buttonType === 'both') {
          return saveProgress;
        }

        if (buttonType === 'eu') {
          const additionalParameters = buttonData.additionalParameters;
          if (!additionalParameters || !additionalParameters.root) {
            return onFormSubmit;
          }
          const action = additionalParameters.root.action;
          switch (action) {
            case 'save':
            default:
              return saveProgress;
          }
        }

        return () => {
          openSnackbar('error', t('customButtons.form.invalid-data.snackbar'));
        };
      },
      [saveProgress, onFormSubmit]
    );

    const onToggleSectionCollapse = useCallback(
      fieldId => {
        let newCollapsedSections = stateRef.current.collapsedSections || {}; // key->values of sectionId and true/false for collapsed
        if (newCollapsedSections[fieldId]) {
          newCollapsedSections[fieldId] = false;
        } else {
          newCollapsedSections[fieldId] = true;
        }
        stateRef.current.collapsedSections = newCollapsedSections;
        updateCollapsibleSections(
          initialFormData.sectionMap,
          newCollapsedSections
        );
      },
      [initialFormData]
    );

    const contextProps = useMemo(
      () => ({
        form: initialFormData.form,
        formId,
        variant,
        registerField,
        getSubmissionsId,
        getReferenceId,
        apiEndpoints,
        initialUserSubmission: initialFormData.userSubmission,
        onFormChange,
        onToggleSectionCollapse,
        validateField,
        ...config,
        isFormReadOnly: config.isFormReadOnly || forceReadOnlyMode.current,
        viewOnlyAdminMode: viewOnlyAdminMode.current,
        mappedFields: initialFormData.mappedFields,
        isSubmitting,
        integrations: initialFormData.integrationsList
      }),
      [
        initialFormData,
        formId,
        variant,
        registerField,
        getSubmissionsId,
        getReferenceId,
        apiEndpoints,
        onFormChange,
        validateField,
        config,
        forceReadOnlyMode.current,
        viewOnlyAdminMode.current
      ]
    );

    const isAnonymous =
      initialFormData.form.sharingType === FormSharingTypes.PUBLIC;

    const viewProps = {
      ...initialFormData,
      onFormChange,
      onFormBlur,
      onFormSubmit,
      onDocusignSubmit,
      onThirdfortSubmit,
      onWorldCheckSubmit,
      saveProgress,
      prevOrNext,
      showTOC,
      formTOCRef,
      hideFormTitle,
      hideFormDescription,
      isEndUserEndpoint,
      topEndPoint,
      bottomEndPoint,
      customCSS,
      stickyBottomBtns,
      stickyTopBtns,
      setIsSubmitted,
      isSubmitted,
      isIgnoreValidationWarningDialogOpen,
      setIsIgnoreValidationWarningDialogOpen,
      ignoreValidationSubmitButtonId,
      setIgnoreValidationSubmitButtonId,
      isCredasWarningDialogOpen,
      setIsCredasWarningDialogOpen,
      isThirdfortWarningDialogOpen,
      setIsThirdfortWarningDialogOpen,
      saveWithoutWarning,
      useAutoSave: config.useAutoSave,
      thankUCmp,
      noSubmit,
      noSaveBtn,
      isFormReadOnly: config.isFormReadOnly || forceReadOnlyMode.current,
      viewOnlyAdminMode: viewOnlyAdminMode.current,
      submitForm,
      variant,
      buttons: viewOnlyAdminMode.current
        ? []
        : mergeButtons(
            initialFormData.buttons.systemButtons,
            initialFormData.buttons.customButtons,
            initialFormData.buttons.integrationButtons
          ),
      requestMoreInfo_TEMP_PROP,
      getCustomButtonOperation,
      isAnonymous
    };

    return (
      <FormContext.Provider value={contextProps}>
        <FormView {...viewProps} ref={formRef} />
      </FormContext.Provider>
    );
  }
);

export default React.memo(FormEngine, _isEqual);
